import { create } from 'zustand';

/**
 * Local preferences kept in localStorage. They drive purely visual
 * choices (density, language). No server-side persistence yet — the UI
 * runs entirely on the operator's browser.
 */

const KEY = 'md.preferences';

export type Density = 'comfortable' | 'compact';
export type UILanguage = 'fr';

type PrefsState = {
  density: Density;
  language: UILanguage;
  autoRefresh: boolean;
  setDensity: (d: Density) => void;
  setLanguage: (l: UILanguage) => void;
  setAutoRefresh: (v: boolean) => void;
};

function read(): { density: Density; language: UILanguage; autoRefresh: boolean } {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { density: 'comfortable', language: 'fr', autoRefresh: true };
    const parsed = JSON.parse(raw) as Partial<PrefsState>;
    return {
      density: (parsed.density as Density) ?? 'comfortable',
      language: (parsed.language as UILanguage) ?? 'fr',
      autoRefresh: parsed.autoRefresh ?? true,
    };
  } catch {
    return { density: 'comfortable', language: 'fr', autoRefresh: true };
  }
}

function persist(state: Pick<PrefsState, 'density' | 'language' | 'autoRefresh'>) {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        density: state.density,
        language: state.language,
        autoRefresh: state.autoRefresh,
      }),
    );
  } catch {
    /* incognito / disabled storage */
  }
}

export const usePreferences = create<PrefsState>((set, get) => ({
  ...read(),
  setDensity: (d) => {
    set({ density: d });
    persist({ ...get(), density: d });
  },
  setLanguage: (l) => {
    set({ language: l });
    persist({ ...get(), language: l });
  },
  setAutoRefresh: (v) => {
    set({ autoRefresh: v });
    persist({ ...get(), autoRefresh: v });
  },
}));
